const SECRET_KEY =
  process.env.REACT_APP_SECURE_LINK_SECRET_KEY || "default-secret-key";

// Helper to convert string to bytes for Web Crypto API
const stringToBytes = (str: string): Uint8Array => {
  const encoder = new TextEncoder();
  return encoder.encode(str);
};

// Helper to convert bytes to hex string
const bytesToHex = (bytes: Uint8Array): string => {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

// Helper to convert bytes to base64 (UTF-8 safe)
const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Helper to convert base64 to bytes (UTF-8 safe)
const base64ToBytes = (str: string): Uint8Array => {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Encodes and signs any data into a URL-safe query string
 * @param data - Any serializable data to encode
 * @returns Query string with encoded data and HMAC signature (format: "d=...&sig=...")
 */
export const encodeData = async <T extends Record<string, any>>(
  data: T,
): Promise<string> => {
  const jsonString = JSON.stringify(data);
  const jsonBytes = stringToBytes(jsonString); // Convert to UTF-8 bytes
  const encoded = bytesToBase64(jsonBytes); // Base64 encode bytes

  // Create HMAC signature using Web Crypto API
  const keyData = stringToBytes(SECRET_KEY);
  const messageData = stringToBytes(encoded);

  const key = await crypto.subtle.importKey(
    "raw",
    keyData as any,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  const signatureBuffer = await crypto.subtle.sign(
    "HMAC",
    key,
    messageData as any,
  );
  const signatureHex = bytesToHex(new Uint8Array(signatureBuffer));

  return `d=${encoded}&sig=${signatureHex}`;
};

/**
 * Verifies and decodes a previously encoded secure link query string
 * @param queryString - Query string from URL (with "d" and "sig" parameters)
 * @returns Decoded data if signature is valid, null otherwise
 */
export const decodeData = async <T extends Record<string, any>>(
  queryString: string,
): Promise<T | null> => {
  try {
    const params = new URLSearchParams(queryString);
    const encoded = params.get("d");
    const signature = params.get("sig");

    if (!encoded || !signature) {
      console.error("Missing data or signature in URL");
      return null;
    }

    // Verify signature
    const keyData = stringToBytes(SECRET_KEY);
    const messageData = stringToBytes(encoded);

    const key = await crypto.subtle.importKey(
      "raw",
      keyData as any,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["verify"],
    );

    const signatureBytes = new Uint8Array(
      signature.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16)),
    );

    const isValid = await crypto.subtle.verify(
      "HMAC",
      key,
      signatureBytes as any,
      messageData as any,
    );

    if (!isValid) {
      console.error(
        "Signature verification failed - data may have been tampered with",
      );
      return null;
    }

    // Decode
    const jsonBytes = base64ToBytes(encoded); // Base64 decode to bytes
    const jsonString = new TextDecoder().decode(jsonBytes); // Convert UTF-8 bytes to string
    const data = JSON.parse(jsonString);
    return data as T;
  } catch (error) {
    console.error("Failed to decode data:", error);
    return null;
  }
};

/**
 * Generates a secure shareable link for encrypted data
 * @param data - Data to encode and sign
 * @param pathname - Path of the page (e.g., "/replicas-payment", "/custom-page")
 * @returns Full URL with encoded data and signature
 */
export const generateLink = async <T extends Record<string, any>>(
  data: T,
  pathname: string,
): Promise<string> => {
  const queryString = await encodeData(data);
  return `${window.location.origin}/${pathname.replace(/^\//, "")}?${queryString}`;
};

// Backward compatibility - aliases for quote-specific usage
export const encodeQuote = encodeData;
export const decodeQuote = decodeData;
export const generateQuoteLink = async <T extends Record<string, any>>(
  data: T,
): Promise<string> => {
  return generateLink(data, "replicas-payment");
};
